import Api from "../apis/Api";
import ActionModalMixin from "./ActionModalMixin";

const GitActionsMixin = {
  mixins: [ActionModalMixin],
  methods: {
    async refreshGitStatus() {
      const response = await Api.get(`/instances/${this.instance.id}/git`);
      this.gitStatus = response.data.data;

      return response;
    },
    async repairRepository() {
      if (this.repairSubmitting) {
        return;
      }

      this.repairSubmitting = true;
      this.errorMessage = null;

      try {
        await Api.post(`/instances/${this.instance.id}/git/repair`);
        await this.refreshGitStatus();
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t("notification.instance.git.repair.success"),
        });
      } catch (error) {
        const code = Api.getErrorCode(error);
        if (code === "git_local_repository_exists") {
          await this.refreshGitStatus().catch(() => {});
        }
        const errorMessage = Api.getErrorMessage(error);
        this.errorMessage = errorMessage;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage,
        });
      } finally {
        this.repairSubmitting = false;
      }
    },
    showPullDirtyTreeModal({ onComplete = null, externalPull = null } = {}) {
      this.resetModal();
      this.modalOptions.title = this.$t("heading.instance.advanced.git.pullDirtyTree.title");
      this.modalOptions.message = this.$t("heading.instance.advanced.git.pullDirtyTree.message");
      this.modalOptions.alert = this.$t("heading.instance.advanced.git.pullDirtyTree.warning");
      this.modalOptions.icon = "$alertwarning";
      this.modalOptions.loadingMode = "active";

      const setActiveButton = (activeButton) => {
        for (const button of this.modalOptions.buttons) {
          if (!button || button.type === "cancel") {
            continue;
          }
          button.loading = false;
          button.disabled = true;
        }

        if (activeButton) {
          activeButton.loading = true;
          activeButton.disabled = false;
        }
      };

      const resetButtonsState = () => {
        for (const button of this.modalOptions.buttons) {
          if (!button || button.type === "cancel") {
            continue;
          }
          button.loading = false;
          button.disabled = false;
        }
      };

      const withModalSubmitting = async (activeButton, action) => {
        if (this.modalOptions.submitting) {
          return false;
        }

        this.modalOptions.persistent = true;
        this.modalOptions.submitting = true;
        setActiveButton(activeButton);

        try {
          await action();
          return true;
        } finally {
          resetButtonsState();
          this.modalOptions.persistent = false;
          this.modalOptions.submitting = false;
        }
      };

      const runPull = async (strategy) => {
        if (typeof externalPull === "function") {
          this.pullSubmitting = true;
          try {
            await externalPull(strategy);
            this.modalClose();
            if (typeof onComplete === "function") {
              await onComplete();
            } else {
              await this.refreshGitStatus();
              await this.fetchGitLogs();
            }
          } finally {
            this.pullSubmitting = false;
          }
          return;
        }

        return this.pullRepository({ strategy, onSuccess: onComplete });
      };

      const revertButton = {
        label: this.$t("heading.instance.advanced.git.drift.revert"),
        color: "error",
        loading: false,
        disabled: false,
        onclick: () =>
          withModalSubmitting(revertButton, async () => {
            const ok = await this.revertRepository();
            if (!ok) {
              return;
            }

            this.modalClose();

            if (typeof onComplete === "function") {
              await onComplete();
            }
          }),
      };

      const forceButton = {
        label: this.$t("heading.instance.advanced.git.pullDirtyTree.force"),
        color: "primary",
        loading: false,
        disabled: false,
        onclick: () => withModalSubmitting(forceButton, () => runPull("force")),
      };

      const pushFirstButton = {
        label:
          Number(this.gitStatus?.commits_behind || 0) > 0
            ? `${this.$t("heading.instance.advanced.git.pullDirtyTree.pushFirst")} — ${this.$t("heading.instance.advanced.git.pullDirtyTree.pushFirstHint")}`
            : this.$t("heading.instance.advanced.git.pullDirtyTree.pushFirst"),
        color: "primary",
        loading: false,
        disabled: false,
        onclick: () => withModalSubmitting(pushFirstButton, () => runPull("push_first")),
      };

      this.modalOptions.buttons.unshift(
        revertButton,
        forceButton,
        pushFirstButton
      );

      this.modalOptions.open = true;
    },
    showPushFirstModal() {
      this.resetModal();
      this.modalOptions.title = this.$t("heading.instance.advanced.git.pushFirst.title");
      this.modalOptions.message = this.$t("heading.instance.advanced.git.pushFirst.message");
      this.modalOptions.icon = "$alertwarning";
      this.modalOptions.buttons.unshift({
        label: this.$t("heading.instance.advanced.git.pushFirst.confirm"),
        color: "primary",
        onclick: () => {
          this.modalClose();
          this.pushRepository();
        },
      });
      this.modalOptions.open = true;
    },
    showDisconnectModal() {
      this.resetModal();
      this.modalOptions.title = this.$t("heading.instance.advanced.git.disconnect.title");
      this.modalOptions.message = this.$t("heading.instance.advanced.git.disconnect.subtitle");
      this.modalOptions.color = "error";
      this.modalOptions.icon = "$alertwarning";
      this.modalOptions.buttons.unshift({
        label: this.$t("heading.instance.advanced.git.disconnect.confirm"),
        color: "error",
        onclick: () => {
          this.disconnectRepository();
        },
      });
      this.modalOptions.open = true;
    },
    async showChangeBranchModal() {
      this.resetModal();
      this.modalOptions.title = this.$t("heading.instance.advanced.git.modal.branch.title");
      this.modalOptions.message = this.$t("heading.instance.advanced.git.modal.branch.message");
      this.modalOptions.alert = this.$t("heading.instance.advanced.git.modal.branch.warning");
      this.modalOptions.icon = "$edit";
      this.modalOptions.formValues = {
        branch: this.gitStatus.branch || "",
      };

      let branchOptions = [];
      try {
        const response = await Api.get(`/instances/${this.instance.id}/git/branches`);
        branchOptions = (response.data.data || []).map((name) => ({
          label: name,
          value: name,
        }));
      } catch (error) {
        branchOptions = [];
      }

      const useSelect = branchOptions.length > 0;

      this.modalOptions.formFields = [
        {
          label: this.$t("heading.instance.advanced.git.modal.branch.field"),
          name: "branch",
          type: useSelect ? "select" : "text",
          options: useSelect ? branchOptions : undefined,
          required: true,
          rules: [
            (v) =>
              !!(v && String(v).trim()) ||
              this.$t("form.validation.required", {
                field: this.$t("heading.instance.advanced.git.modal.branch.field"),
              }),
            (v) =>
              (v && String(v).trim().length <= 255) ||
              this.$t("form.validation.maxLength", {
                field: this.$t("heading.instance.advanced.git.modal.branch.field"),
                length: 255,
              }),
          ],
        },
      ];
      this.modalOptions.buttons.unshift({
        label: this.$t("heading.instance.advanced.git.modal.branch.confirm"),
        color: "primary",
        onclick: (modal) => {
          modal.$refs.form.validate() && this.changeBranch(modal.formValues);
        },
      });
      this.modalOptions.open = true;
    },
    async changeBranch(formData) {
      if (this.modalOptions.submitting) {
        return;
      }

      const branch = (formData.branch || "").trim();
      if (!branch) {
        return;
      }

      this.modalOptions.persistent = true;
      this.modalOptions.submitting = true;
      this.modalOptions.submittingSuccess = "";
      this.modalOptions.submittingError = "";
      this.errorMessage = null;

      try {
        await Api.put(`/instances/${this.instance.id}/change-branch`, { branch });
        this.modalClose();
        await this.refreshGitStatus();
        await this.fetchGitLogs();
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t("notification.instance.git.branch.success"),
        });
      } catch (error) {
        const errorMessage = Api.getErrorMessage(error);
        this.modalOptions.submittingError = errorMessage;
        this.errorMessage = errorMessage;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage,
        });
      } finally {
        this.modalOptions.persistent = false;
        this.modalOptions.submitting = false;
      }
    },
    async pullRepository({ strategy = null, onSuccess = null } = {}) {
      if (this.pullSubmitting) {
        return;
      }

      this.pullSubmitting = true;
      this.errorMessage = null;

      try {
        await Api.post(`/instances/${this.instance.id}/git/pull`, strategy ? { strategy } : {});
        this.modalClose();

        if (typeof onSuccess === "function") {
          await onSuccess();
        } else {
          await this.refreshGitStatus();
          await this.fetchGitLogs();
          await this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.$t("notification.instance.git.pull.success"),
          });
        }
      } catch (error) {
        const code = Api.getErrorCode(error);
        const errorMessage = Api.getErrorMessage(error);
        this.errorMessage = errorMessage;

        if (strategy == null && code === "git_working_tree_dirty") {
          this.showPullDirtyTreeModal();
          return;
        }

        if (code === "git_remote_branch_missing") {
          this.showPushFirstModal();
          return;
        }

        if (code === "git_invalid_token" || code === "git_access_denied") {
          await this.refreshGitStatus().catch(() => {});
        }

        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage,
        });
      } finally {
        this.pullSubmitting = false;
      }
    },
    async pushRepository() {
      if (this.pushSubmitting) {
        return;
      }

      if (this.isRemoteAheadOfSite) {
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: this.$t("heading.instance.advanced.git.push.pullFirstHint"),
        });
        return;
      }

      this.pushSubmitting = true;
      this.errorMessage = null;

      try {
        const response = await Api.post(`/instances/${this.instance.id}/git/push`);
        await this.refreshGitStatus();
        await this.fetchGitLogs();
        const nothingToPush = response.data?.nothing_to_push === true;
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: nothingToPush
            ? this.$t("notification.instance.git.push.nothing")
            : this.$t("notification.instance.git.push.success"),
        });
      } catch (error) {
        const code = Api.getErrorCode(error);
        const errorMessage = Api.getErrorMessage(error);
        this.errorMessage = errorMessage;

        if (code === "git_invalid_token" || code === "git_access_denied") {
          await this.refreshGitStatus().catch(() => {});
        }

        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage,
        });
      } finally {
        this.pushSubmitting = false;
      }
    },
    async revertRepository() {
      if (this.revertSubmitting) {
        return false;
      }

      this.revertSubmitting = true;
      this.errorMessage = null;

      try {
        await Api.post(`/instances/${this.instance.id}/git/revert`);
        await this.refreshGitStatus();
        await this.fetchGitLogs();
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t("notification.instance.git.revert.success"),
        });
        return true;
      } catch (error) {
        const errorMessage = Api.getErrorMessage(error);
        this.errorMessage = errorMessage;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage,
        });
        return false;
      } finally {
        this.revertSubmitting = false;
      }
    },
    async checkForUpdates() {
      if (this.checkSubmitting) {
        return;
      }

      this.checkSubmitting = true;
      this.errorMessage = null;

      try {
        await this.refreshGitStatus();
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t("notification.instance.git.check.success"),
        });
      } catch (error) {
        const errorMessage = Api.getErrorMessage(error);
        this.errorMessage = errorMessage;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage,
        });
      } finally {
        this.checkSubmitting = false;
      }
    },
    async ignoreDrift() {
      if (this.ignoreDriftSubmitting) {
        return;
      }

      this.ignoreDriftSubmitting = true;

      try {
        await Api.post(`/instances/${this.instance.id}/git/drift/ignore`);
        await this.fetchGitStatus({ silent: true });
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t("notification.instance.git.drift.ignore.success"),
        });
      } catch (error) {
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage: Api.getErrorMessage(error),
        });
      } finally {
        this.ignoreDriftSubmitting = false;
      }
    },
    async updateGitCredentials() {
      if (this.credentialsSubmitting || !(this.credentialsToken || "").trim()) {
        return;
      }

      this.credentialsSubmitting = true;
      this.errorMessage = null;

      try {
        const response = await Api.put(`/instances/${this.instance.id}/git/credentials`, {
          token: (this.credentialsToken || "").trim(),
          auth_type: "pat",
        });
        this.gitStatus = response.data.data;
        this.credentialsToken = "";
        this.showTokenUpdate = false;
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t("notification.instance.git.credentials.success"),
        });
      } catch (error) {
        const errorMessage = Api.getErrorMessage(error);
        this.errorMessage = errorMessage;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage,
        });
      } finally {
        this.credentialsSubmitting = false;
      }
    },
    async updateAutoDeploy(enabled) {
      if (this.autoDeploySubmitting) {
        return;
      }

      this.autoDeploySubmitting = true;
      this.errorMessage = null;

      try {
        const response = await Api.put(`/instances/${this.instance.id}/auto-deploy`, {
          auto_deploy_enabled: enabled === true,
        });
        this.gitStatus = response.data.data;
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t("notification.instance.git.autoDeploy.success"),
        });

        if (this.isAutoDeployInProgress && typeof this.startAutoDeployPolling === "function") {
          this.startAutoDeployPolling();
        }
      } catch (error) {
        const errorMessage = Api.getErrorMessage(error);
        this.errorMessage = errorMessage;
        await this.fetchGitStatus({ silent: true });
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage,
        });
      } finally {
        this.autoDeploySubmitting = false;
      }
    },
    async disconnectRepository() {
      if (this.modalOptions.submitting) return;

      this.modalOptions.persistent = true;
      this.modalOptions.submitting = true;
      this.modalOptions.submittingSuccess = "";
      this.modalOptions.submittingError = "";
      this.errorMessage = null;

      try {
        await Api.post(`/instances/${this.instance.id}/git/disconnect`);
        this.modalClose();
        await this.fetchGitStatus({ silent: true });
        await this.$store.dispatch("addAlert", {
          success: true,
          successMessage: this.$t("notification.instance.git.disconnect.success"),
        });
      } catch (error) {
        const errorMessage = Api.getErrorMessage(error);
        this.modalOptions.submittingError = errorMessage;
        this.errorMessage = errorMessage;
        await this.$store.dispatch("addAlert", {
          success: false,
          errorMessage,
        });
      } finally {
        this.modalOptions.persistent = false;
        this.modalOptions.submitting = false;
      }
    },
  },
};

export default GitActionsMixin;
